import { Table as AntdTable } from "antd";
import { TableColumnType } from "components/UI/Table/types/table.ts";
import { MoneyCell } from "components/UI/Table/UI/MoneyCell/MoneyCell.tsx";
import { PercentCell } from "components/UI/Table/UI/PercentCell/PercentCell.tsx";
import { Identifiable, NonIdentifiable } from "types/models.ts";
import { formatNumber } from "utils/formatNumber.ts";

import MoneyData = App.Data.MoneyData;

const normalizeDataIndex = (dataIndex: string | readonly string[] | number | undefined): string => {
    if (typeof dataIndex === "string") {
        return dataIndex;
    }
    if (Array.isArray(dataIndex)) {
        return dataIndex.join(".");
    }
    return String(dataIndex);
};

// TODO - refactor summaryData to be defined config (number/string/money/percent)
export const generateSummary = <T extends Identifiable | NonIdentifiable>(
    summaryData: unknown,
    keys: string[],
    percentKeys: string[] = [],
    totalLabel: string,
    visibleColumns?: TableColumnType<T>[],
    originalColumnConfig?: TableColumnType<T>[],
    withRowSelection?: boolean
) => {
    if (!summaryData || !visibleColumns) {
        return null;
    }

    const ensureValue = (value: unknown) => (typeof value === "number" ? value : 0);

    const isMoneyData = (value: unknown): value is MoneyData => {
        return (
            typeof value === "object" &&
            value !== null &&
            "amount" in value &&
            "currency_id" in value &&
            typeof (value as any).amount === "number" &&
            typeof (value as any).currency_id === "number"
        );
    };

    // If visibleColumns is provided, use their order and dataIndex values
    // Otherwise fall back to the original keys parameter
    const columnsToRender = visibleColumns
        ? visibleColumns
            .map((col) => normalizeDataIndex(col.dataIndex))
            .filter((dataIndex) => keys.includes(dataIndex))
        : keys;

    const totals = columnsToRender.reduce((acc: Record<string, React.ReactNode>, key) => {
        const record = summaryData as Record<string, unknown>;
        const value = record[key];

        if (isMoneyData(value)) {
            acc[key] = <MoneyCell money={value} />;
        } else if (percentKeys.includes(key)) {
            acc[key] = <PercentCell amount={ensureValue(value)} />;
        } else {
            acc[key] = formatNumber(ensureValue(value));
        }

        return acc;
    }, {});

    // Check if the first column from the original configuration is still visible
    const firstOriginalColumn = originalColumnConfig?.[0];
    const firstOriginalDataIndex = firstOriginalColumn
        ? normalizeDataIndex(firstOriginalColumn.dataIndex)
        : null;

    // Find the current position of the first original column in the visible columns
    const firstColumnCurrentIndex = firstOriginalDataIndex
        ? visibleColumns?.findIndex((col) => normalizeDataIndex(col.dataIndex) === firstOriginalDataIndex)
        : -1;

    // Only show Total if the first original column is still visible
    const shouldShowTotal = firstColumnCurrentIndex !== undefined && firstColumnCurrentIndex >= 0;

    return (
        <AntdTable.Summary fixed="bottom">
            <AntdTable.Summary.Row className="font-bold">
                {withRowSelection && <AntdTable.Summary.Cell key="rowSelection" index={0} />}
                {/* Render all cells based on visible columns */}
                {visibleColumns?.map((column, index) => {
                    const actualIndex = withRowSelection ? index + 1 : index;
                    const dataIndex = normalizeDataIndex(column.dataIndex);

                    // If this is the position where the original first column is now located, show Total
                    if (shouldShowTotal && index === firstColumnCurrentIndex) {
                        return (
                            <AntdTable.Summary.Cell
                                key="total"
                                index={actualIndex}
                                className="truncate overflow-hidden text-ellipsis"
                            >
                                <b>{totalLabel}</b>
                            </AntdTable.Summary.Cell>
                        );
                    }

                    // For other positions, show summary data if available
                    const hasSummaryData = keys.includes(dataIndex);
                    return (
                        <AntdTable.Summary.Cell
                            key={dataIndex}
                            index={actualIndex}
                            colSpan={1}
                            className="truncate overflow-hidden text-ellipsis"
                        >
                            {hasSummaryData ? totals[dataIndex] : ""}
                        </AntdTable.Summary.Cell>
                    );
                })}
            </AntdTable.Summary.Row>
        </AntdTable.Summary>
    );
};
